import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/Register.css';

// 소셜 로그인 추가 정보 입력 모달 컴포넌트
const SocialInfoModal = ({ isOpen, onClose, onSubmit, socialData }) => {
  const [formData, setFormData] = useState({
    firstName: socialData.firstName || '',
    lastName: socialData.lastName || '',
    birthDate: '',
    gender: '',
    phoneNumber: '',
    nationality: '',
    residenceCountry: ''
  });
  
  const { 
    firstName, 
    lastName, 
    birthDate, 
    gender, 
    phoneNumber, 
    nationality, 
    residenceCountry 
  } = formData;
  
  const onChange = e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };
  
  const handleSubmit = e => {
    e.preventDefault();
    onSubmit({
      ...socialData,
      ...formData
    });
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h3>추가 정보 입력</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <p>회원가입을 완료하기 위해 추가 정보를 입력해주세요.</p>
          <form onSubmit={handleSubmit} autoComplete="on">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="modal-lastName">성</label>
                <input
                  type="text"
                  id="modal-lastName"
                  name="lastName"
                  value={lastName}
                  onChange={onChange}
                  required
                  autoComplete="family-name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="modal-firstName">이름</label>
                <input
                  type="text"
                  id="modal-firstName"
                  name="firstName"
                  value={firstName}
                  onChange={onChange}
                  required
                  autoComplete="given-name"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="modal-birthDate">생년월일</label>
              <input
                type="date"
                id="modal-birthDate"
                name="birthDate"
                value={birthDate}
                onChange={onChange}
                required
                autoComplete="bday"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="modal-gender">성별</label>
              <select
                id="modal-gender"
                name="gender"
                value={gender}
                onChange={onChange}
                required
                autoComplete="sex"
              >
                <option value="">선택하세요</option>
                <option value="male">남성</option>
                <option value="female">여성</option>
                <option value="other">기타</option>
                <option value="prefer_not_to_say">응답하지 않음</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="modal-phoneNumber">전화번호</label>
              <input
                type="tel"
                id="modal-phoneNumber"
                name="phoneNumber"
                value={phoneNumber}
                onChange={onChange}
                placeholder="예: 010-1234-5678"
                required
                autoComplete="tel"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="modal-nationality">국적</label>
              <select
                id="modal-nationality"
                name="nationality"
                value={nationality}
                onChange={onChange}
                required
                autoComplete="country"
              >
                <option value="">선택하세요</option>
                <option value="KR">대한민국</option>
                <option value="JP">일본</option>
                <option value="US">미국</option>
                <option value="CN">중국</option>
                <option value="other">기타</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="modal-residenceCountry">거주 국가</label>
              <select
                id="modal-residenceCountry"
                name="residenceCountry"
                value={residenceCountry}
                onChange={onChange}
                required
                autoComplete="country"
              >
                <option value="">선택하세요</option>
                <option value="KR">대한민국</option>
                <option value="JP">일본</option>
                <option value="US">미국</option>
                <option value="CN">중국</option>
                <option value="other">기타</option>
              </select>
            </div>
            
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="modal-agreeTerms"
                name="agreeTerms"
                required
              />
              <label htmlFor="modal-agreeTerms">
                <Link to="/terms" target="_blank">이용약관</Link>에 동의합니다
              </label>
            </div>
            
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="modal-agreePrivacy"
                name="agreePrivacy"
                required
              />
              <label htmlFor="modal-agreePrivacy">
                <Link to="/privacy" target="_blank">개인정보처리방침</Link>에 동의합니다
              </label>
            </div>
            
            <button type="submit" className="btn btn-primary">
              가입 완료
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// 회원가입 방식 선택 컴포넌트
const RegisterMethodSelector = ({ onSelectMethod, isLoading }) => {
  return (
    <div className="register-method-container">
      <h2>회원가입 방식 선택</h2>
      <p className="register-method-description">
        미라이쥬쿠에 오신 것을 환영합니다! 회원가입 방식을 선택해주세요.
      </p>
      
      <div className="register-method-options">
        <button 
          className="register-method-option email-option"
          onClick={() => onSelectMethod('email')}
          disabled={isLoading}
        >
          <i className="fas fa-envelope"></i>
          <span>이메일로 회원가입</span>
          <p>이메일 주소와 비밀번호로 새 계정을 만듭니다.</p>
        </button>
        
        <div className="register-method-divider">
          <span>또는</span>
        </div>
        
        <div className="register-method-social">
          <p>소셜 계정으로 간편하게 가입</p>
          <div className="social-buttons">
            <button 
              className="btn-social btn-google" 
              onClick={() => onSelectMethod('google')}
              disabled={isLoading}
            >
              <i className="fab fa-google"></i> Google
            </button>
            <button 
              className="btn-social btn-instagram" 
              onClick={() => onSelectMethod('instagram')}
              disabled={isLoading}
            >
              <i className="fab fa-instagram"></i> Instagram
            </button>
            <button 
              className="btn-social btn-twitter" 
              onClick={() => onSelectMethod('twitter')}
              disabled={isLoading}
            >
              <i className="fab fa-x-twitter"></i> X
            </button>
          </div>
        </div>
      </div>
      
      <div className="register-links">
        <Link to="/login">이미 계정이 있으신가요? 로그인</Link>
      </div>
    </div>
  );
};

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    password2: '',
    birthDate: '',
    gender: '',
    phoneNumber: '',
    nationality: '',
    residenceCountry: '',
    agreeTerms: false,
    agreePrivacy: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/dashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isAuthenticated, googleLogin, kakaoLogin, naverLogin, redirectPath: contextRedirectPath } = useAuth();
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [socialLoginData, setSocialLoginData] = useState(null);
  const [registerMethod, setRegisterMethod] = useState(null); // 'email', 'google', 'kakao', 'naver' 중 하나

  // 리디렉션 경로 가져오기
  const getRedirectPath = () => {
    // URL 파라미터에서 리디렉션 경로 확인
    const params = new URLSearchParams(location.search);
    const redirectParam = params.get('redirect');
    
    // 이전 페이지 경로 (state로 전달된 경우)
    const fromPath = location.state?.from || '';
    
    // URL 파라미터 > state로 전달된 경로 > 저장된 경로 > 기본 경로(대시보드) 순으로 우선순위
    return redirectParam || fromPath || contextRedirectPath || '/dashboard';
  };

  // 컴포넌트 마운트 시 리디렉션 경로 저장 및 소셜 로그인 데이터 확인
  useEffect(() => {
    // 리디렉션 경로 미리 저장
    const redirectTo = getRedirectPath();
    if (redirectTo && redirectTo !== '/dashboard') {
      setRedirectPath(redirectTo);
    }
    
    // 이미 로그인된 경우 리디렉션
    if (isAuthenticated) {
      navigate(getRedirectPath());
      return;
    }
    
    // 소셜 로그인 데이터 확인 (Login 페이지에서 전달된 경우)
    if (location.state?.socialLoginData) {
      console.log('소셜 로그인 데이터 확인:', location.state.socialLoginData);
      setSocialLoginData(location.state.socialLoginData);
      setRegisterMethod(location.state.socialLoginData.provider);
      setShowSocialModal(true);
    }
  }, [isAuthenticated, navigate, location.search, contextRedirectPath, location.state]);

  const { 
    firstName, 
    lastName, 
    email, 
    password, 
    password2, 
    birthDate, 
    gender, 
    phoneNumber, 
    nationality, 
    residenceCountry, 
    agreeTerms, 
    agreePrivacy 
  } = formData;

  const onChange = e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const onSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    // 입력값 검증
    if (!firstName || !lastName || !email || !password || !password2 || !birthDate || !gender || !phoneNumber || !nationality || !residenceCountry) {
      setError('모든 필수 필드를 입력해주세요.');
      setIsLoading(false);
      return;
    }

    // 이메일 유효성 검증
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setError('유효한 이메일 주소를 입력해주세요.');
      setIsLoading(false);
      return;
    }

    if (password !== password2) {
      setError('비밀번호가 일치하지 않습니다.');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      setIsLoading(false);
      return;
    }

    if (!agreeTerms) {
      setError('이용약관에 동의해주세요.');
      setIsLoading(false);
      return;
    }

    if (!agreePrivacy) {
      setError('개인정보처리방침에 동의해주세요.');
      setIsLoading(false);
      return;
    }

    try {
      const result = await register({
        firstName,
        lastName,
        email,
        password,
        birthDate,
        gender,
        phoneNumber,
        nationality,
        residenceCountry
      });
      if (result.success) {
        setSuccess(result.message);
        // 3초 후 로그인 페이지로 리디렉션
        setTimeout(() => {
          navigate('/login?registerSuccess=true');
        }, 3000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('회원가입 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // 회원가입 방식 선택 처리
  const handleSelectMethod = (method) => {
    // 인스타그램과 X는 바로 경고 메시지만 표시
    if (method === 'instagram') {
      setError('인스타그램 로그인 기능은 현재 개발 중입니다.');
      return;
    } else if (method === 'twitter') {
      setError('X(구 트위터) 로그인 기능은 현재 개발 중입니다.');
      return;
    }
    
    // 다른 방식은 registerMethod 상태 변경
    setRegisterMethod(method);
    
    // 소셜 로그인 방식 선택 시 바로 해당 로그인 처리 실행
    if (method === 'google') {
      handleGoogleLogin();
    } else if (method === 'kakao') {
      handleKakaoLogin();
    } else if (method === 'naver') {
      handleNaverLogin();
    }
    // 이메일 방식은 registerMethod 상태만 변경하여 폼 표시
  };

  // Google 로그인 처리
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      setError('');
      
      try {
        // Google API에서 사용자 정보 가져오기
        const userInfoResponse = await axios.get(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );
        
        const userData = userInfoResponse.data;
        
        if (!userData || !userData.email) {
          setError('Google에서 사용자 정보를 가져올 수 없습니다. 다시 시도해주세요.');
          setIsLoading(false);
          setRegisterMethod(null); // 선택 화면으로 돌아가기
          return;
        }
        
        // 이름 분리 (성과 이름)
        let firstName = userData.given_name || '';
        let lastName = userData.family_name || '';
        
        // 이름이 없는 경우 전체 이름에서 분리 시도
        if (!firstName || !lastName) {
          const nameParts = userData.name.split(' ');
          if (nameParts.length > 1) {
            lastName = nameParts[0];
            firstName = nameParts.slice(1).join(' ');
          } else {
            firstName = userData.name;
            lastName = '';
          }
        }
        
        // 소셜 로그인 데이터 저장 및 모달 표시
        setSocialLoginData({
          token: tokenResponse.access_token,
          email: userData.email,
          firstName: firstName,
          lastName: lastName,
          googleId: userData.sub
        });
        
        setShowSocialModal(true);
        setIsLoading(false);
      } catch (err) {
        console.error('Google login error:', err);
        
        // 에러 타입에 따른 메시지 설정
        if (err.response) {
          // 서버 응답이 있는 경우
          if (err.response.status === 404) {
            setError('서버에 연결할 수 없습니다. API URL이 올바른지 확인해주세요.');
          } else if (err.response.status === 400) {
            setError(err.response.data?.msg || '이미 다른 방식으로 가입된 이메일입니다.');
          } else if (err.response.status === 500) {
            setError('서버 오류가 발생했습니다. 나중에 다시 시도해주세요.');
          } else {
            setError(`Google 로그인 중 오류가 발생했습니다: ${err.response.data?.msg || err.message}`);
          }
        } else if (err.request) {
          // 요청은 보냈지만 응답이 없는 경우
          setError('서버에 연결할 수 없습니다. 인터넷 연결을 확인해주세요.');
        } else {
          // 요청 설정 중 오류가 발생한 경우
          setError(`Google 로그인 중 오류가 발생했습니다: ${err.message}`);
        }
        setIsLoading(false);
        setRegisterMethod(null); // 선택 화면으로 돌아가기
      }
    },
    onError: error => {
      console.error('Google login error:', error);
      setError(`Google 로그인 초기화 중 오류가 발생했습니다: ${error.error_description || error.message || '알 수 없는 오류'}`);
      setRegisterMethod(null); // 선택 화면으로 돌아가기
    },
    onNonOAuthError: () => {
      // 사용자가 팝업을 닫거나 취소한 경우
      console.log('Google login canceled by user');
      setIsLoading(false);
      setRegisterMethod(null); // 선택 화면으로 돌아가기
    },
    flow: 'implicit', // 명시적으로 implicit 플로우 사용
    cancel_on_tap_outside: true // 외부 클릭 시 취소 허용
  });

  // 소셜 로그인 추가 정보 제출 처리
  const handleSocialInfoSubmit = async (completeData) => {
    setIsLoading(true);
    
    try {
      // 리디렉션 경로 미리 저장
      const redirectTo = getRedirectPath();
      console.log('Social Login - 리디렉션 경로:', redirectTo);
      
      let result = { success: false };
      
      // 소셜 로그인 유형에 따라 적절한 함수 호출
      if (completeData.googleId) {
        // Google 로그인
        result = await googleLogin(completeData.token, completeData);
      } else if (completeData.kakaoId) {
        // Kakao 로그인
        result = await kakaoLogin(completeData.token, completeData);
      } else if (completeData.naverId) {
        // Naver 로그인
        result = await naverLogin(completeData.token, completeData);
      } else {
        setError('지원되지 않는 소셜 로그인 유형입니다.');
        setIsLoading(false);
        setShowSocialModal(false);
        return;
      }
      
      if (result.success) {
        // 로그인 성공 시 리디렉션
        navigate(redirectTo);
      } else {
        // 실패 시 에러 메시지 표시
        setError(result.message || '소셜 계정으로 로그인하는데 실패했습니다. 다시 시도해주세요.');
        setShowSocialModal(false);
      }
    } catch (err) {
      console.error('Social login error:', err);
      setError('소셜 로그인 중 오류가 발생했습니다.');
      setShowSocialModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Kakao 로그인 처리
  const handleKakaoLogin = () => {
    setIsLoading(true);
    setError('');
    
    // Kakao SDK가 로드되었는지 확인
    if (!window.Kakao) {
      setError('카카오 SDK를 로드할 수 없습니다. 다시 시도해주세요.');
      setIsLoading(false);
      return;
    }
    
    // 카카오 로그인 요청
    window.Kakao.Auth.login({
      success: (authObj) => {
        // 사용자 정보 요청
        window.Kakao.API.request({
          url: '/v2/user/me',
          success: (res) => {
            const kakaoAccount = res.kakao_account;
            
            if (!kakaoAccount || !kakaoAccount.email) {
              setError('카카오 계정에서 이메일 정보를 가져올 수 없습니다. 이메일 제공에 동의해주세요.');
              setIsLoading(false);
              return;
            }
            
            // 이름 정보 가져오기
            let name = '';
            if (kakaoAccount.profile && kakaoAccount.profile.nickname) {
              name = kakaoAccount.profile.nickname;
            }
            
            // 이름 분리 (성과 이름)
            let firstName = '';
            let lastName = '';
            
            if (name) {
              const nameParts = name.split(' ');
              if (nameParts.length > 1) {
                lastName = nameParts[0];
                firstName = nameParts.slice(1).join(' ');
              } else {
                firstName = name;
                lastName = '';
              }
            }
            
            // 소셜 로그인 데이터 저장 및 모달 표시
            setSocialLoginData({
              token: authObj.access_token,
              email: kakaoAccount.email,
              firstName: firstName,
              lastName: lastName,
              kakaoId: res.id
            });
            
            setShowSocialModal(true);
            setIsLoading(false);
          },
          fail: (error) => {
            console.error('Kakao API request failed:', error);
            setError('카카오 계정 정보를 가져오는데 실패했습니다.');
            setIsLoading(false);
          }
        });
      },
      fail: (error) => {
        console.error('Kakao login failed:', error);
        setError('카카오 로그인에 실패했습니다.');
        setIsLoading(false);
      }
    });
  };

  // Naver 로그인 처리
  const handleNaverLogin = () => {
    setIsLoading(true);
    setError('');
    
    // 네이버 로그인 객체가 있는지 확인
    if (!window.naver || !window.naver.LoginWithNaverId) {
      setError('네이버 로그인 SDK를 로드할 수 없습니다. 다시 시도해주세요.');
      setIsLoading(false);
      return;
    }
    
    // 네이버 로그인 인스턴스 초기화
    const naverLogin = new window.naver.LoginWithNaverId({
      clientId: process.env.REACT_APP_NAVER_CLIENT_ID,
      callbackUrl: `${window.location.origin}/auth/naver/callback`,
      isPopup: true,
      loginButton: { color: 'green', type: 3, height: 60 }
    });
    
    naverLogin.init();
    
    // 팝업 창으로 네이버 로그인 실행
    naverLogin.getLoginStatus((status) => {
      if (status) {
        // 이미 로그인된 상태
        const { email, name, id } = naverLogin.user;
        
        if (!email) {
          setError('네이버 계정에서 이메일 정보를 가져올 수 없습니다. 이메일 제공에 동의해주세요.');
          setIsLoading(false);
          return;
        }
        
        // 이름 분리 (성과 이름)
        let firstName = '';
        let lastName = '';
        
        if (name) {
          const nameParts = name.split(' ');
          if (nameParts.length > 1) {
            lastName = nameParts[0];
            firstName = nameParts.slice(1).join(' ');
          } else {
            firstName = name;
            lastName = '';
          }
        }
        
        // 소셜 로그인 데이터 저장 및 모달 표시
        setSocialLoginData({
          token: naverLogin.accessToken.accessToken,
          email: email,
          firstName: firstName,
          lastName: lastName,
          naverId: id
        });
        
        setShowSocialModal(true);
      } else {
        // 로그인 필요
        naverLogin.authorize();
      }
      setIsLoading(false);
    });
  };

  // 회원가입 방식 선택 화면으로 돌아가기
  const handleBackToMethodSelection = () => {
    setRegisterMethod(null);
    setError('');
  };

  return (
    <div className="register-container">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-text">처리 중입니다...</div>
        </div>
      )}
      {!registerMethod ? (
        // 회원가입 방식 선택 화면
        <>
          {error && <div className="alert alert-danger register-alert">{error}</div>}
          <RegisterMethodSelector 
            onSelectMethod={handleSelectMethod} 
            isLoading={isLoading} 
          />
        </>
      ) : registerMethod === 'email' ? (
        // 이메일 회원가입 폼
        <div className="register-form-container">
          <h2>회원가입</h2>
          <button 
            className="back-button" 
            onClick={handleBackToMethodSelection}
            disabled={isLoading}
          >
            <i className="fas fa-arrow-left"></i> 회원가입 방식 선택으로 돌아가기
          </button>
          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          <form onSubmit={onSubmit} autoComplete="on">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="lastName">성</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={lastName}
                  onChange={onChange}
                  required
                  autoComplete="family-name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="firstName">이름</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={firstName}
                  onChange={onChange}
                  required
                  autoComplete="given-name"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="email">이메일</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={onChange}
                required
                autoComplete="email"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="birthDate">생년월일</label>
              <input
                type="date"
                id="birthDate"
                name="birthDate"
                value={birthDate}
                onChange={onChange}
                required
                autoComplete="bday"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="gender">성별</label>
              <select
                id="gender"
                name="gender"
                value={gender}
                onChange={onChange}
                required
                autoComplete="sex"
              >
                <option value="">선택하세요</option>
                <option value="male">남성</option>
                <option value="female">여성</option>
                <option value="other">기타</option>
                <option value="prefer_not_to_say">응답하지 않음</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="phoneNumber">전화번호</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={phoneNumber}
                onChange={onChange}
                placeholder="예: 010-1234-5678"
                required
                autoComplete="tel"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="nationality">국적</label>
              <select
                id="nationality"
                name="nationality"
                value={nationality}
                onChange={onChange}
                required
                autoComplete="country"
              >
                <option value="">선택하세요</option>
                <option value="KR">대한민국</option>
                <option value="JP">일본</option>
                <option value="US">미국</option>
                <option value="CN">중국</option>
                <option value="other">기타</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="residenceCountry">거주 국가</label>
              <select
                id="residenceCountry"
                name="residenceCountry"
                value={residenceCountry}
                onChange={onChange}
                required
                autoComplete="country"
              >
                <option value="">선택하세요</option>
                <option value="KR">대한민국</option>
                <option value="JP">일본</option>
                <option value="US">미국</option>
                <option value="CN">중국</option>
                <option value="other">기타</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="password">비밀번호</label>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={onChange}
                required
                autoComplete="new-password"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password2">비밀번호 확인</label>
              <input
                type="password"
                id="password2"
                name="password2"
                value={password2}
                onChange={onChange}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="agreeTerms"
                name="agreeTerms"
                checked={agreeTerms}
                onChange={onChange}
                required
              />
              <label htmlFor="agreeTerms">
                <Link to="/terms" target="_blank">이용약관</Link>에 동의합니다
              </label>
            </div>
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="agreePrivacy"
                name="agreePrivacy"
                checked={agreePrivacy}
                onChange={onChange}
                required
              />
              <label htmlFor="agreePrivacy">
                <Link to="/privacy" target="_blank">개인정보처리방침</Link>에 동의합니다
              </label>
            </div>
            <div className="form-group">
              <button 
                type="submit" 
                className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`}
                disabled={isLoading || !agreeTerms || !agreePrivacy}
              >
                {isLoading ? ' ' : '회원가입'}
              </button>
            </div>
          </form>
          
          <div className="register-links">
            <Link to="/login">이미 계정이 있으신가요? 로그인</Link>
          </div>
        </div>
      ) : (
        // 소셜 로그인 선택 시 로딩 표시
        <div className="register-form-container">
          <h2>소셜 계정으로 회원가입</h2>
          <button 
            className="back-button" 
            onClick={handleBackToMethodSelection}
            disabled={isLoading}
          >
            <i className="fas fa-arrow-left"></i> 회원가입 방식 선택으로 돌아가기
          </button>
          {error && <div className="alert alert-danger">{error}</div>}
          {!showSocialModal && !error && (
            <div className="social-login-loading">
              <div className="loading-spinner"></div>
              <p>{registerMethod === 'google' ? '구글' : registerMethod === 'instagram' ? '인스타그램' : registerMethod === 'twitter' ? 'X' : '카카오'} 계정으로 로그인 중입니다...</p>
            </div>
          )}
        </div>
      )}
      
      {/* 소셜 로그인 추가 정보 모달 */}
      <SocialInfoModal 
        isOpen={showSocialModal}
        onClose={() => {
          setShowSocialModal(false);
          setRegisterMethod(null);
        }}
        onSubmit={handleSocialInfoSubmit}
        socialData={socialLoginData || {}}
      />
    </div>
  );
};

export default Register; 